/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import ErrorBoundary from '@/utils/ErrorBoundray';

import { useKahu } from '@/hooks/useKahu';
import { useKahuDockState } from '@/hooks/useKahuDockState';

import { KahuIcons } from '../icons/icons';
import { KahuChattingErrorUi } from './kahu-chatting-error-ui';
import { KahuChattingInterface } from './kahu-chatting-interface';
import { KahuMinimized } from './kahu-minimized';

const MIN_WIDTH = 380;
const MIN_HEIGHT = 250;

// Resize handle: top edge, left edge, or top-left corner.
// The bottom-right corner is always the fixed anchor.
type ResizeHandle = 'top' | 'left' | 'top-left';

export const KahuInvocationPoint = () => {
  const {
    messages: kahuMessage,
    goToConversationList,
    activeConversationId,
    createNewConversation,
    setDraftPrompt,
    isSending,
    currentView,
  } = useKahu();

  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenCount, setLastSeenCount] = useState(kahuMessage.length);
  const [isHovering, setIsHovering] = useState(false);

  const { isDocked, setDocked, size, setSize } = useKahuDockState();

  const [windowState, setWindowState] = useState<'open' | 'minimized'>(
    isDocked ? 'open' : 'minimized'
  );

  // Tracks in-flight resize values — written on every mouse move,
  // committed to React state only on mouseup (zero re-renders during motion).
  const liveSizeRef = useRef({ width: size.width, height: size.height });

  // Refs for resize operations to avoid re-renders on mouse move
  const isResizingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width: 0, height: 0 });
  const resizeHandleRef = useRef<ResizeHandle | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Stable event handlers — created once, never recreated.
  // They delegate to *ImplRef which is always updated to the latest closure.
  // This ensures window.removeEventListener always removes the correct function.

  const mouseMoveImplRef = useRef<(e: MouseEvent) => void>(() => {});
  const mouseUpImplRef = useRef<() => void>(() => {});

  const stableMouseMove = useRef((e: MouseEvent) =>
    mouseMoveImplRef.current(e)
  ).current;
  const stableMouseUp = useRef(() => mouseUpImplRef.current()).current;

  const openMinimizedWindow = useMemo(
    () => windowState === 'minimized',
    [windowState]
  );

  // Set while handling a prompt-prefill open request, so the effect below
  // doesn't immediately bounce the freshly created conversation back to the list.
  const skipAutoListRef = useRef(false);

  // Kept in sync every render so the stable 'open-kahu-chat' listener below
  // always sees the latest value without needing to re-subscribe.
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  const isSendingRef = useRef(isSending);
  isSendingRef.current = isSending;

  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;

  // When opening the chat window, decide which view to show
  useEffect(() => {
    if (windowState === 'open' && !activeConversationId) {
      if (skipAutoListRef.current) {
        skipAutoListRef.current = false;
        return;
      }
      goToConversationList();
    }
  }, [windowState, activeConversationId, goToConversationList]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail;
      if (detail?.prompt) {
        const hasResolvedConversation = !!activeConversationIdRef.current;
        const isResumingLiveSend =
          isSendingRef.current && currentViewRef.current === 'chat';

        if (!hasResolvedConversation && !isResumingLiveSend) {
          skipAutoListRef.current = true;
          createNewConversation();
        } else if (isResumingLiveSend) {
          // Send in flight and the user is still on that chat (window may
          // just be minimized) — reopen as-is, don't reset anything.
          skipAutoListRef.current = true;
        }
        setDraftPrompt(detail.prompt);
      }
      setWindowState('open');
    };

    window.addEventListener('open-kahu-chat', handler);
    return () => window.removeEventListener('open-kahu-chat', handler);
  }, [createNewConversation, setDraftPrompt]);

  useEffect(() => {
    // window minimized
    if (windowState === 'minimized') {
      const diff = kahuMessage.length - lastSeenCount;

      if (diff > 0) {
        setUnreadCount((prev) => prev + diff);
        setLastSeenCount(kahuMessage.length);
      }
    }

    // window opened
    if (windowState === 'open') {
      setUnreadCount(0);
      setLastSeenCount(kahuMessage.length);
    }
  }, [kahuMessage.length, windowState, lastSeenCount]);

  // Update the impl refs on every render so they always close over the latest
  // state without recreating the stable listeners registered on the window.
  mouseMoveImplRef.current = (e: MouseEvent) => {
    if (!isResizingRef.current) return;

    // Prevent text selection while resizing
    e.preventDefault();

    requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;

      // Delta from where the mouse button was pressed
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;

      let newWidth = startSizeRef.current.width;
      let newHeight = startSizeRef.current.height;

      // Left edge or top-left corner: dragging left (negative dx) widens the window.
      // The right edge stays anchored, so we subtract dx.
      if (
        resizeHandleRef.current === 'left' ||
        resizeHandleRef.current === 'top-left'
      ) {
        newWidth = startSizeRef.current.width - dx;
      }

      // Top edge or top-left corner: dragging up (negative dy) tallens the window.
      // The bottom edge stays anchored, so we subtract dy.
      if (
        resizeHandleRef.current === 'top' ||
        resizeHandleRef.current === 'top-left'
      ) {
        newHeight = startSizeRef.current.height - dy;
      }

      // Clamp: minimum dimensions and maximum viewport dimensions
      const clampedWidth = Math.max(
        MIN_WIDTH,
        Math.min(newWidth, window.innerWidth - 54)
      );
      const clampedHeight = Math.max(
        MIN_HEIGHT,
        Math.min(newHeight, window.innerHeight - 48)
      );

      liveSizeRef.current = { width: clampedWidth, height: clampedHeight };
      // Apply directly to DOM — no React re-render on every frame
      el.style.width = `${clampedWidth}px`;
      el.style.height = `${clampedHeight}px`;
    });
  };

  mouseUpImplRef.current = () => {
    // Commit in-flight size to React state (one localStorage write)
    if (isResizingRef.current) {
      setSize(liveSizeRef.current);
    }

    isResizingRef.current = false;
    document.body.style.userSelect = '';

    window.removeEventListener('mousemove', stableMouseMove);
    window.removeEventListener('mouseup', stableMouseUp);
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    handle: ResizeHandle
  ) => {
    e.stopPropagation();

    const el = containerRef.current;
    if (!el) return;

    // Default start dimensions — valid for floating mode where size is already
    // tracked by the hook.
    let startWidth = size.width;
    let startHeight = size.height;

    if (isDocked) {
      // Docked layout uses h-full via CSS, so hook's size values are stale.
      // Snapshot the actual rendered rect BEFORE any state change so we have
      // the true pixel dimensions to hand off to the floating container.
      const rect = el.getBoundingClientRect();
      startWidth = rect.width;
      startHeight = rect.height;

      // React 18 batches these two setState calls into ONE re-render.
      // On that render: isDocked=false + size=captured → floatingStyle renders
      // at exactly the same dimensions the docked panel had -> zero visual jump.
      setSize({ width: startWidth, height: startHeight });
      setDocked(false);
    }

    // Register listeners BEFORE the batched re-render fires. The first
    // mousemove event will find isResizingRef=true and the correct start size,
    // so the drag continues seamlessly without a second click.
    isResizingRef.current = true;
    resizeHandleRef.current = handle;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = { width: startWidth, height: startHeight };
    liveSizeRef.current = { width: startWidth, height: startHeight };
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', stableMouseMove);
    window.addEventListener('mouseup', stableMouseUp);
  };

  const pathname = usePathname();
  if (pathname === '/en') return null;

  // The window is always anchored to the bottom-right corner of the viewport.
  // Width and height are only applied when the chat panel is open — when
  // minimized the container auto-sizes to the bubble button.
  const floatingStyle: React.CSSProperties = {
    bottom: '24px',
    right: '27px',
    ...(windowState === 'open'
      ? { width: `${size.width}px`, height: `${size.height}px` }
      : {}),
  };

  return (
    <div
      ref={containerRef}
      className={`fixed z-50 flex flex-col items-end gap-4 ${isDocked ? 'bottom-0 right-0 mb-2 mr-2 h-full pt-5' : ''}`}
      style={!isDocked ? floatingStyle : { width: `${size.width}px` }}
    >
      {windowState === 'open' && (
        <ErrorBoundary
          fallback={
            <KahuChattingErrorUi
              onClose={() => {
                setDocked(false);
                setWindowState('minimized');
              }}
            />
          }
        >
          <KahuChattingInterface
            setWindowState={setWindowState}
            onResizeMouseDown={handleResizeMouseDown}
          />
        </ErrorBoundary>
      )}

      {/* handle minimized window  */}
      {openMinimizedWindow && unreadCount > 0 && isHovering && (
        <KahuMinimized unreadCount={unreadCount} />
      )}

      {/*  Show the kahu invocation point, if the chat window is closed or minimized. */}
      {windowState === 'minimized' && (
        <div
          className="relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full
        bg-[linear-gradient(150deg,#34BCAF_15.28%,#257C9D_82.73%)]
        shadow-[0_0_16px_0_rgba(52,188,175,0.64)]"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={() => {
            if (windowState === 'minimized') {
              setWindowState('open');
              return;
            }
          }}
        >
          {KahuIcons.KahuInvocationIcon()}

          {openMinimizedWindow && unreadCount > 0 && (
            <span className="absolute -right-[6px] -top-[3px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-notification-error-dark px-0.5 py-px text-body-sm font-medium text-white shadow-[0_0_0_3px_#0000003D]">
              {unreadCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
